import { defineStore } from 'pinia';
import { claimTrialFund, getTrialWithdrawConditions } from '@/api/trial-fund';
import {
  TRIAL_FUND_CLAIM_AMOUNT,
  TRIAL_FUND_CORRECT_SIDE,
  TRIAL_FUND_GUIDE_ENABLED,
  TRIAL_FUND_GUIDE_STEPS,
  TRIAL_FUND_GUEST_VIEW_SESSION_KEY,
  TRIAL_FUND_LOGGED_VIEW_SESSION_KEY,
  TRIAL_FUND_SHARES,
  TRIAL_FUND_SIM_PATH,
  TRIAL_FUND_STAKE_USD,
  TRIAL_FUND_STYLE_PREVIEW,
  TRIAL_FUND_WIN_PAYOUT_USD,
  resolveTrialFundRoundWindow,
} from '@/constants/trial-fund';
import {
  clearTrialFundPendingClaimAfterWin,
  clearTrialFundSimPending,
  clearTrialFundUserStorage,
  readShowTrialFundFeedCard,
  readTrialFundClaimedSeen,
  readTrialFundExchanged,
  readTrialFundGuideDone,
  readTrialFundPendingClaimAfterWin,
  readTrialFundSimPending,
  setShowTrialFundFeedCard,
  setTrialFundClaimedSeen,
  setTrialFundGuideDone,
  setTrialFundPendingClaimAfterWin,
  writeTrialFundSimPending,
} from '@/utils/trial-fund-storage';
import { trackAnalyticsLog } from '@/utils/analytics-track';
import { shouldSkipGuestPromptForRoute } from '@/utils/quiet-shell-route.js';
import {
  tryTrackOnboardingGuideOutcome,
  tryTrackOnboardingGuideStart,
} from '@/utils/analytics-conversion-extras';
import { resolveHasTrialFund, tryTrackAuthRegisterOnce } from '@/utils/analytics-auth-register';
import { getClobNowMs } from '@/domain/crypto/polymarket-clob-clock.js';
import { getUserId } from '@/utils/auth-token.js';
import { getUserProfile } from '@/api/user';
import { resolveTrialClaimUserMessage, isTrialClaimDepositRequiredError } from '@/utils/trial-claim-message';

/** @typedef {'closed' | 'mini' | 'modal'} TrialFundPromptView */
/** @typedef {'idle' | 'pending' | 'settling' | 'settled'} TrialFundSimPhase */
/** @typedef {typeof TRIAL_FUND_GUIDE_STEPS[number] | 'dismissConfirm' | 'dismissBye' | ''} TrialFundGuideStep */

let tryOpenLoggedInflight = false;
let expandLoggedInflight = false;
let simRoundTimerId = 0;
let simSettleOnce = false;
let simSettleInflight = false;
/** @type {Promise<boolean> | null} */
let resumeGuideAfterAuthInflight = null;
/** @type {{ tickSimRound: () => void } | null} */
let simWatcherStore = null;

function onSimDocumentVisible() {
  if (typeof document === 'undefined') return;
  if (document.visibilityState !== 'visible') return;
  simWatcherStore?.tickSimRound();
}

/** 当前是否在福利盘模拟页（避免进盘自动叠登录后大窗） */
function isOnTrialFundSimRoute() {
  if (typeof window === 'undefined') return false;
  const path = String(window.location.pathname || '');
  return path === TRIAL_FUND_SIM_PATH || path.startsWith(`${TRIAL_FUND_SIM_PATH}/`);
}

function isGuideBlockingOtherPrompts(state) {
  return (
    Boolean(state.guideOpen) ||
    Boolean(state.guidePausedForAuth) ||
    Boolean(state.guidePausedForResult)
  );
}

function formatSimCountdownLabel(remainSec) {
  const sec = Math.max(0, Math.floor(Number(remainSec) || 0));
  const mm = String(Math.floor(sec / 60)).padStart(2, '0');
  const ss = String(sec % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

function readErrMessage(err) {
  return String(err?.message ?? err?.msg ?? '').trim();
}

/** 已进入领取后生命周期 */
function hasTrialFundClaimLifecycle(conditions) {
  return readTrialFundClaimedSeen() || readTrialFundExchanged() || Boolean(conditions?.claimed);
}

/**
 * 首页福利盘 Feed 卡：未领取 / 未兑换才展示。
 * 用 claimedSeen / exchanged 本地标记做快路径，避免 conditions 未回时闪一下。
 */
function isTrialFundHomeFeedCardEligible(conditions) {
  if (readTrialFundExchanged()) return false;
  if (readTrialFundClaimedSeen()) return false;
  if (conditions?.claimed) return false;
  return true;
}

function toNum(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function normalizeConditions(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const hasRealDepositRaw = raw.hasRealDeposit;
  /** null = 后端未下发 / 未知，勿当 false 误拦已充值用户 */
  let hasRealDeposit = null;
  if (hasRealDepositRaw === true || hasRealDepositRaw === false) {
    hasRealDeposit = Boolean(hasRealDepositRaw);
  } else if (hasRealDepositRaw === 1 || hasRealDepositRaw === 0) {
    hasRealDeposit = Boolean(hasRealDepositRaw);
  } else if (hasRealDepositRaw === 'true' || hasRealDepositRaw === 'false') {
    hasRealDeposit = hasRealDepositRaw === 'true';
  }
  return {
    claimed: Boolean(raw.claimed),
    claimedAmount: toNum(raw.claimedAmount),
    hasRealDeposit,
    depositReached: Boolean(raw.depositReached),
    depositAmount: toNum(raw.depositAmount),
    depositThreshold: toNum(raw.depositThreshold, 50),
    tradeVolumeReached: Boolean(raw.tradeVolumeReached),
    tradeVolumeAmount: toNum(raw.tradeVolumeAmount),
    tradeVolumeThreshold: toNum(raw.tradeVolumeThreshold),
    marketCountReached: Boolean(raw.marketCountReached),
    marketCountAmount: toNum(raw.marketCountAmount),
    marketCountThreshold: toNum(raw.marketCountThreshold, 3),
    activeDaysReached: Boolean(raw.activeDaysReached),
    activeDaysAmount: toNum(raw.activeDaysAmount),
    activeDaysThreshold: toNum(raw.activeDaysThreshold, 5),
    riskCheckPassed: raw.riskCheckPassed !== false,
    canExchange: Boolean(raw.canExchange),
    failedCondition: toNum(raw.failedCondition),
    msg: String(raw.msg ?? '').trim(),
    isTrialUser: Boolean(raw.isTrialUser),
    bypassTrialConditions: Boolean(raw.bypassTrialConditions),
  };
}

/** 明确无真实充值（字段已知为 false）才前端硬拦 */
function isKnownMissingDeposit(conditions) {
  return Boolean(conditions) && conditions.hasRealDeposit === false && !conditions.claimed;
}

function readGuestMiniFromSession() {
  if (typeof sessionStorage === 'undefined') return false;
  return sessionStorage.getItem(TRIAL_FUND_GUEST_VIEW_SESSION_KEY) === 'mini';
}

function persistGuestMiniSession() {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(TRIAL_FUND_GUEST_VIEW_SESSION_KEY, 'mini');
}

function clearGuestMiniSession() {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(TRIAL_FUND_GUEST_VIEW_SESSION_KEY);
}

function readLoggedMiniFromSession() {
  if (typeof sessionStorage === 'undefined') return false;
  return sessionStorage.getItem(TRIAL_FUND_LOGGED_VIEW_SESSION_KEY) === 'mini';
}

function persistLoggedMiniSession() {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(TRIAL_FUND_LOGGED_VIEW_SESSION_KEY, 'mini');
}

function clearLoggedMiniSession() {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(TRIAL_FUND_LOGGED_VIEW_SESSION_KEY);
}

export const useTrialFundStore = defineStore('trialFund', {
  state: () => ({
    /** @type {TrialFundPromptView} */
    guestView: /** @type {TrialFundPromptView} */ ('closed'),
    /** @type {TrialFundPromptView} */
    loggedView: /** @type {TrialFundPromptView} */ ('closed'),
    showFeedCard: readShowTrialFundFeedCard(),
    resultOpen: false,
    /** @type {'win' | 'lose' | null} */
    resultKind: null,
    resultChosenSide: /** @type {'yes' | 'no' | null} */ (null),
    conditions: null,
    conditionsLoading: false,
    dusdBalance: 0,
    /** @type {'pending' | 'confirmed' | ''} */
    claimMintStatus: '',
    claimLoading: false,
    /** 赢面领取因未充值被拦：结果窗展示去充值 CTA */
    claimNeedsDeposit: false,
    /** 已赢未领：关结果窗后充值成功仍可自动再领（不依赖 resultOpen） */
    pendingClaimAfterWin: false,
    /** 弹窗内操作反馈（SurfaceHint） */
    surfaceHint: '',
    surfaceHintTone: /** @type {'success' | 'error' | 'info'} */ ('info'),
    /** 模拟盘回合（全局：离详情仍可倒计时 / 结算领取） */
    simPhase: /** @type {TrialFundSimPhase} */ ('idle'),
    simLockedSide: /** @type {'yes' | 'no' | null} */ (null),
    simLockedShares: 0,
    simSettleAtMs: 0,
    simRoundEndAtMs: 0,
    simResolvedSide: /** @type {'yes' | 'no' | null} */ (null),
    simNowMs: Date.now(),
    /** H5 游戏式引导 */
    guideOpen: false,
    /** @type {TrialFundGuideStep} */
    guideStep: '',
    /** 关闭确认前记住的步骤，便于「再想想」 */
    guideStepBeforeDismiss: '',
    /** 下单唤起登录时暂关引导，记住步骤以便恢复 */
    guidePausedForAuth: false,
    /** @type {TrialFundGuideStep} */
    guideStepBeforeAuth: '',
    /** 引导中弹出输赢结果窗时暂关引导 */
    guidePausedForResult: false,
    /** @type {TrialFundGuideStep} */
    guideStepBeforeResult: '',
  }),

  getters: {
    hasClaimed: (state) => Boolean(state.conditions?.claimed),
    /** 体验金领取硬闸：须有真实充值（与后端 HasRealDeposit 对齐；null=未知） */
    hasRealDeposit: (state) => state.conditions?.hasRealDeposit === true,
    stakeUsd: () => TRIAL_FUND_STAKE_USD,
    winPayoutUsd: () => TRIAL_FUND_WIN_PAYOUT_USD,
    claimAmount: () => TRIAL_FUND_CLAIM_AMOUNT,
    /**
     * 首页 Feed 福利盘卡（含未登录访客；下单/领取在模拟页再鉴权）。
     * 不依赖「稍后再说」开关做资格判断；void showFeedCard 仅用于 applyShowFeedCard 时触发重算
     *（claimedSeen / exchanged 写在 localStorage，需借 state 使 getter 失效）。
     */
    shouldShowHomeFeedCard: (state) => {
      void state.showFeedCard;
      return isTrialFundHomeFeedCardEligible(state.conditions);
    },
    guestModalOpen: (state) => !isGuideBlockingOtherPrompts(state) && state.guestView === 'modal',
    guestMiniOpen: (state) => !isGuideBlockingOtherPrompts(state) && state.guestView === 'mini',
    /** @deprecated 用 guestModalOpen */
    guestOpen: (state) => !isGuideBlockingOtherPrompts(state) && state.guestView === 'modal',
    loggedModalOpen: (state) => !isGuideBlockingOtherPrompts(state) && state.loggedView === 'modal',
    /**
     * 右侧小窗（FC）：
     * - 已领取体验金后隐藏（资金已进普通余额）
     * - 未领取：关大窗后保持展示；点击由 expandLoggedPrompt 分流
     * - 模拟盘 pending/settling：强制露出，供 fc 上方倒计时挂载
     * - 游戏式引导进行中 / 登录或结果暂关：隐藏，避免抢占 / 挡高亮
     */
    loggedMiniOpen: (state) => {
      if (isGuideBlockingOtherPrompts(state)) return false;
      if (state.simPhase === 'pending' || state.simPhase === 'settling') return true;
      if (hasTrialFundClaimLifecycle(state.conditions)) return false;
      return state.loggedView === 'mini';
    },
    /** @deprecated 用 loggedModalOpen */
    loggedOpen: (state) => !isGuideBlockingOtherPrompts(state) && state.loggedView === 'modal',
    simHasPendingPosition: (state) => state.simPhase === 'pending' && state.simLockedSide != null,
    simCountdownSec: (state) => {
      if (state.simPhase === 'settling' || state.simPhase === 'settled') return 0;
      return Math.max(0, Math.ceil((state.simRoundEndAtMs - state.simNowMs) / 1000));
    },
    simCountdownLabel: (state) => {
      if (state.simPhase === 'settling' || state.simPhase === 'settled') return '';
      const sec = Math.max(0, Math.ceil((state.simRoundEndAtMs - state.simNowMs) / 1000));
      if (sec <= 0 && state.simPhase !== 'idle') return '';
      return formatSimCountdownLabel(sec);
    },
    /** fc 上方倒计时：仅 pending 展示 */
    simMiniCountdownLabel: (state) => {
      if (state.simPhase !== 'pending') return '';
      const sec = Math.max(0, Math.ceil((state.simSettleAtMs - state.simNowMs) / 1000));
      return formatSimCountdownLabel(sec);
    },
    guideActive: (state) => state.guideOpen && Boolean(state.guideStep),
    /** 引导进行中，或登录/结果窗暂关阶段：禁止其它自动弹窗抢占 */
    guideBlocksOtherPrompts: (state) => isGuideBlockingOtherPrompts(state),
  },

  actions: {
    clearSurfaceHint() {
      this.surfaceHint = '';
    },

    setSurfaceHint(message, tone = 'info') {
      this.surfaceHint = String(message || '').trim();
      this.surfaceHintTone = tone;
    },

    /**
     * 赢面待领：持久化供充值/Privy 后自动签领。
     * @param {{ requireDeposit?: boolean }} [opts]
     *   requireDeposit=true 才亮「去充值」CTA；纯赢面落标记时勿误开充值按钮（已充值用户会闪一下）。
     */
    markPendingClaimAfterWin(opts = {}) {
      this.pendingClaimAfterWin = true;
      if (opts.requireDeposit) this.claimNeedsDeposit = true;
      setTrialFundPendingClaimAfterWin(true);
      console.info('[trial-fund] pending claim after win marked', {
        requireDeposit: Boolean(opts.requireDeposit),
        userId: String(getUserId() || ''),
      });
    },

    clearPendingClaimAfterWin() {
      this.pendingClaimAfterWin = false;
      this.claimNeedsDeposit = false;
      clearTrialFundPendingClaimAfterWin();
    },

    /** 内存或持久化任一为真即视为待自动领取 */
    hasPendingClaimAfterWin() {
      return Boolean(this.pendingClaimAfterWin || readTrialFundPendingClaimAfterWin());
    },

    /** 仅恢复 pending 标记；是否展示充值 CTA 由 conditions.hasRealDeposit 决定 */
    hydratePendingClaimAfterWinFromStorage() {
      if (readTrialFundPendingClaimAfterWin()) {
        this.pendingClaimAfterWin = true;
      }
    },

    syncClaimNeedsDepositFromConditions() {
      if (this.hasClaimed || readTrialFundClaimedSeen()) {
        this.claimNeedsDeposit = false;
        return;
      }
      if (!this.hasPendingClaimAfterWin()) {
        this.claimNeedsDeposit = false;
        return;
      }
      this.claimNeedsDeposit = isKnownMissingDeposit(this.conditions);
    },

    tryOpenGuestPrompt(options = {}) {
      if (typeof window === 'undefined') return false;
      if (this.guideOpen || this.guidePausedForAuth || this.guidePausedForResult) return false;
      if (this.guestView !== 'closed') return true;
      if (TRIAL_FUND_STYLE_PREVIEW) {
        this.guestView = 'modal';
        trackAnalyticsLog({
          key: 'trial_fund.open',
          label: '打开试玩金',
          value: { audience: 'guest', view: 'modal' },
        });
        return true;
      }
      const routePath = String(options.routePath ?? window.location.pathname ?? '');
      if (
        shouldSkipGuestPromptForRoute({
          routePath,
          fullscreenRoute: options.fullscreenRoute,
          quietShell: options.quietShell,
        })
      ) {
        return false;
      }
      if (readGuestMiniFromSession()) {
        this.guestView = 'mini';
        trackAnalyticsLog({
          key: 'trial_fund.open',
          label: '打开试玩金',
          value: { audience: 'guest', view: 'mini' },
        });
        return true;
      }
      this.guestView = 'modal';
      trackAnalyticsLog({
        key: 'trial_fund.open',
        label: '打开试玩金',
        value: { audience: 'guest', view: 'modal' },
      });
      return true;
    },

    /** 关大窗 → 右侧小图 */
    closeGuestPrompt() {
      this.guestView = 'mini';
      persistGuestMiniSession();
    },

    expandGuestPrompt() {
      this.guestView = 'modal';
      trackAnalyticsLog({
        key: 'trial_fund.open',
        label: '打开试玩金',
        value: { audience: 'guest', view: 'modal', source: 'expand' },
      });
    },

    /** 点登录：关大窗，保留右侧小图（取消登录后仍可见） */
    closeGuestPromptForLogin() {
      this.guestView = 'mini';
      persistGuestMiniSession();
    },

    /** 登录成功：清掉未登录态引导 */
    dismissGuestPrompt() {
      this.guestView = 'closed';
      clearGuestMiniSession();
    },

    /** 已领取后关闭福利入口；余额统一由普通 DUSD 余额展示 */
    reconcileLoggedMiniWithMetric() {
      if (this.conditions?.claimed) {
        setTrialFundClaimedSeen(true);
      }
      if (hasTrialFundClaimLifecycle(this.conditions)) {
        this.loggedView = 'closed';
        clearLoggedMiniSession();
        this.applyShowFeedCard(false);
      }
    },

    async tryOpenLoggedPrompt() {
      if (this.guideOpen || this.guidePausedForAuth || this.guidePausedForResult) return;
      if (this.loggedView !== 'closed' || tryOpenLoggedInflight) return;
      if (readTrialFundExchanged() && !TRIAL_FUND_STYLE_PREVIEW) {
        this.syncFeedCardVisibility();
        this.reconcileLoggedMiniWithMetric();
        return;
      }
      tryOpenLoggedInflight = true;
      try {
        if (TRIAL_FUND_STYLE_PREVIEW) {
          // 福利盘内不自动弹大窗，仅留 FC，需手动点开
          this.loggedView = isOnTrialFundSimRoute() ? 'mini' : 'modal';
          if (this.loggedView === 'mini') persistLoggedMiniSession();
          trackAnalyticsLog({
            key: 'trial_fund.open',
            label: '打开试玩金',
            value: { audience: 'logged', view: this.loggedView },
          });
          return;
        }
        try {
          await this.refreshConditions();
        } catch {
          // 条件接口失败仍展示登录后福利弹窗，避免新用户完全看不到引导
        }

        // 冷启动已登录：注册时间窗口内 + 未领试用金 → auth.register（与登录引导去重）
        try {
          const profile = await getUserProfile();
          tryTrackAuthRegisterOnce({
            createTime: profile?.createTime,
            hasTrialFund: resolveHasTrialFund(this.conditions?.claimed),
          });
        } catch (err) {
          console.warn('[trial-fund] auth.register profile failed', err);
        }

        if (readTrialFundExchanged()) {
          this.reconcileLoggedMiniWithMetric();
          return;
        }
        if (this.conditions?.claimed) {
          setTrialFundClaimedSeen(true);
          this.applyShowFeedCard(false);
          this.loggedView = 'closed';
          clearLoggedMiniSession();
          this.restoreSimPendingFromSession();
          return;
        }
        if (this.conditions != null && hasTrialFundClaimLifecycle(this.conditions)) {
          this.loggedView = 'closed';
          clearLoggedMiniSession();
          this.restoreSimPendingFromSession();
          return;
        }
        // 未领取：首页 Feed 卡稳定展示（不依赖是否点过「稍后再说」）
        this.applyShowFeedCard(true);
        if (readLoggedMiniFromSession()) {
          this.loggedView = 'mini';
          this.restoreSimPendingFromSession();
          return;
        }
        // 福利盘页：不自动弹大窗，只露 FC，用户点 fc 再 expand
        if (isOnTrialFundSimRoute()) {
          this.loggedView = 'mini';
          persistLoggedMiniSession();
          this.restoreSimPendingFromSession();
          return;
        }
        this.loggedView = 'modal';
        trackAnalyticsLog({
          key: 'trial_fund.open',
          label: '打开试玩金',
          value: { audience: 'logged', view: 'modal' },
        });
        this.restoreSimPendingFromSession();
      } finally {
        tryOpenLoggedInflight = false;
      }
    },

    /** 进入福利盘：若已有登录后大窗，收成 FC，避免叠在模拟盘上 */
    collapseLoggedModalOnSimEnter() {
      if (this.loggedView !== 'modal') return;
      this.loggedView = 'mini';
      persistLoggedMiniSession();
    },

    /** 关闭大窗 → 右侧 mini（无「今日不再提示」） */
    closeLoggedPrompt({ showFeedCard = false } = {}) {
      if (hasTrialFundClaimLifecycle(this.conditions)) {
        this.loggedView = 'closed';
        clearLoggedMiniSession();
      } else {
        this.loggedView = 'mini';
        persistLoggedMiniSession();
      }
      // 未领取时关窗一律保留/打开首页卡；showFeedCard 参数兼容旧调用
      if (
        (showFeedCard || isTrialFundHomeFeedCardEligible(this.conditions)) &&
        !this.conditions?.claimed
      ) {
        this.applyShowFeedCard(true);
      }
    },

    /**
     * 点右侧 mini（hero-fc）：
     * - 未领取 → 福利大窗（去领取 / 去查看 DSCoin）
     * - 已领取 → 关闭福利入口，余额统一显示在账户余额
     * 先按本地标记立刻开窗，条件接口后台刷新（避免点 fc 卡在 await）。
     */
    async expandLoggedPrompt() {
      if (expandLoggedInflight) return;
      expandLoggedInflight = true;
      try {
        if (hasTrialFundClaimLifecycle(this.conditions)) {
          this.loggedView = 'closed';
          clearLoggedMiniSession();
          return;
        }

        const claimedLocal = hasTrialFundClaimLifecycle(this.conditions);

        if (claimedLocal) {
          this.loggedView = 'closed';
          clearLoggedMiniSession();
          return;
        }

        this.loggedView = 'modal';
        persistLoggedMiniSession();
        trackAnalyticsLog({
          key: 'trial_fund.open',
          label: '打开试玩金',
          value: { audience: 'logged', view: 'modal', source: 'expand' },
        });
        void this.refreshConditions()
          .then(() => {
            if (hasTrialFundClaimLifecycle(this.conditions)) {
              this.loggedView = 'closed';
              clearLoggedMiniSession();
            }
          })
          .catch(() => {
            /* 条件失败仍保留已打开的引导大窗 */
          });
      } finally {
        expandLoggedInflight = false;
      }
    },

    /** 统一写 showFeedCard */
    applyShowFeedCard(show) {
      this.showFeedCard = Boolean(show);
      setShowTrialFundFeedCard(Boolean(show));
    },

    syncFeedCardVisibility() {
      if (!isTrialFundHomeFeedCardEligible(this.conditions)) {
        this.applyShowFeedCard(false);
        return;
      }
      // 未领取：保持展示；兼容旧 localStorage，缺省也打开
      this.applyShowFeedCard(true);
    },

    dismissFeedCard() {
      // 仅领取/兑换后应隐藏；保留方法避免旧调用报错，改为按资格同步
      this.syncFeedCardVisibility();
    },

    openResultDialog({ kind, chosenSide }) {
      // 引导打开或登录暂关时不弹；结果暂关阶段允许（输赢窗本身）
      if (this.guideOpen || this.guidePausedForAuth) return;
      this.resultKind = kind === 'lose' ? 'lose' : 'win';
      this.resultChosenSide = chosenSide === 'no' ? 'no' : 'yes';
      this.resultOpen = true;
    },

    closeResultDialog() {
      this.resultOpen = false;
      // 保留 pendingClaimAfterWin / claimNeedsDeposit：关窗后充值仍可自动再领
    },

    async refreshConditions() {
      this.conditionsLoading = true;
      try {
        const raw = await getTrialWithdrawConditions();
        this.conditions = normalizeConditions(raw);
        // Align local claimedSeen with BE: first-deposit ACTIVITY_DUSD must not sticky-hide FAB.
        if (this.conditions?.claimed) setTrialFundClaimedSeen(true);
        else setTrialFundClaimedSeen(false);
        this.syncFeedCardVisibility();
        this.reconcileLoggedMiniWithMetric();
        this.hydratePendingClaimAfterWinFromStorage();
        this.syncClaimNeedsDepositFromConditions();
        if (!hasTrialFundClaimLifecycle(this.conditions) && this.loggedView === 'closed') {
          this.loggedView = 'mini';
          persistLoggedMiniSession();
        }
        // 登录后拉到已领取：关掉引导并记完成，避免卡在引导层
        this.finishGuideIfAlreadyClaimed();
      } catch (err) {
        console.warn('[trial-fund] withdraw-conditions failed', err);
        throw err;
      } finally {
        this.conditionsLoading = false;
      }
    },

    /**
     * @returns {Promise<{ ok: boolean, alreadyClaimed?: boolean, message?: string, mintStatus?: string, needDeposit?: boolean }>}
     */
    async claimAfterWin() {
      if (this.claimLoading) return { ok: false, message: '' };
      this.claimLoading = true;
      this.clearSurfaceHint();
      const recoverBalanceFromPortfolio = async () => {
        try {
          const { usePortfolioBalanceStore } = await import('@/stores/portfolioBalance');
          const portfolio = usePortfolioBalanceStore();
          await portfolio.refreshAfterTrade();
          return toNum(portfolio.platformAvailable);
        } catch {
          return 0;
        }
      };
      try {
        // Prefer fresh conditions so post-deposit retries see hasRealDeposit=true.
        try {
          await this.refreshConditions();
        } catch {
          /* keep prior conditions */
        }
        if (isKnownMissingDeposit(this.conditions)) {
          const message = resolveTrialClaimUserMessage({
            code: 400,
            message: '请先完成首次充值后再领取体验金',
          });
          this.markPendingClaimAfterWin({ requireDeposit: true });
          this.setSurfaceHint(message, 'info');
          return { ok: false, needDeposit: true, message };
        }

        let result = null;
        let lastErr = null;
        for (let attempt = 0; attempt < 3; attempt += 1) {
          try {
            result = await claimTrialFund({ amount: TRIAL_FUND_CLAIM_AMOUNT });
            lastErr = null;
            break;
          } catch (err) {
            lastErr = err;
            const message = readErrMessage(err);
            const pending = /正在发放|请稍后重试|grant\s*pending|try\s*again/i.test(message);
            if (!pending || attempt >= 2) break;
            await new Promise((resolve) => {
              setTimeout(resolve, 1500 * (attempt + 1));
            });
          }
        }
        if (lastErr) throw lastErr;

        let balance = toNum(result?.balance);
        const mintStatus = String(result?.mintStatus || '')
          .trim()
          .toLowerCase();
        setTrialFundClaimedSeen(true);
        await Promise.allSettled([
          this.refreshConditions(),
          import('@/stores/portfolioBalance').then(({ usePortfolioBalanceStore }) =>
            usePortfolioBalanceStore().refreshAfterTrade()
          ),
        ]);
        if (!(balance > 0)) {
          balance = await recoverBalanceFromPortfolio();
        }
        // Claim succeeded but portfolio lag / empty payload: never show $0.00 on the win sheet.
        if (!(balance > 0)) balance = TRIAL_FUND_CLAIM_AMOUNT;
        this.dusdBalance = balance;
        this.claimMintStatus = mintStatus || 'pending';
        this.applyShowFeedCard(false);
        this.loggedView = 'closed';
        clearLoggedMiniSession();
        this.clearPendingClaimAfterWin();
        trackAnalyticsLog({
          key: 'trial_fund.claim',
          label: '领取试玩金',
          value: {
            alreadyClaimed: false,
            amount: TRIAL_FUND_CLAIM_AMOUNT,
            txHash: String(result?.txHash || '').trim() || undefined,
            mintStatus: mintStatus || undefined,
          },
        });
        console.info('[trial-fund] claim after win ok', {
          mintStatus: mintStatus || 'pending',
          amount: TRIAL_FUND_CLAIM_AMOUNT,
        });
        void import('@/api/welcome-pack')
          .then(({ claimPendingWelcomePackIfAny }) => claimPendingWelcomePackIfAny())
          .catch(() => {});
        return { ok: true, mintStatus: mintStatus || 'pending' };
      } catch (err) {
        let userMessage = resolveTrialClaimUserMessage(err);
        if (isTrialClaimDepositRequiredError(err)) {
          this.markPendingClaimAfterWin({ requireDeposit: true });
          this.setSurfaceHint(userMessage, 'info');
          return { ok: false, needDeposit: true, message: userMessage };
        }
        const noTradeAddress = /no_trade_address|先绑定|reward\.no_trade_address/i.test(
          readErrMessage(err)
        );
        if (noTradeAddress) {
          // 邮箱用户未开通 Privy：等同待充值后自动领，勿要求用户回盘口再点
          this.markPendingClaimAfterWin({ requireDeposit: true });
          const depositMsg =
            userMessage ||
            resolveTrialClaimUserMessage({
              code: 400,
              message: '请先完成首次充值后再领取体验金',
            });
          this.setSurfaceHint(depositMsg, 'info');
          return { ok: false, needDeposit: true, message: depositMsg };
        }
        const alreadyClaimed = /已经领取|already\s*claim/i.test(readErrMessage(err));
        if (alreadyClaimed) {
          setTrialFundClaimedSeen(true);
          this.clearPendingClaimAfterWin();
          await Promise.allSettled([
            this.refreshConditions(),
            import('@/stores/portfolioBalance').then(({ usePortfolioBalanceStore }) =>
              usePortfolioBalanceStore().refreshAfterTrade()
            ),
          ]);
          try {
            const available = await recoverBalanceFromPortfolio();
            this.dusdBalance = available > 0 ? available : TRIAL_FUND_CLAIM_AMOUNT;
          } catch {
            this.dusdBalance = TRIAL_FUND_CLAIM_AMOUNT;
          }
          this.applyShowFeedCard(false);
          this.loggedView = 'closed';
          clearLoggedMiniSession();
          trackAnalyticsLog({
            key: 'trial_fund.claim',
            label: '领取试玩金',
            value: { alreadyClaimed: true },
          });
          return { ok: true, alreadyClaimed: true, message: userMessage };
        }

        const recovered = await recoverBalanceFromPortfolio();
        if (recovered >= TRIAL_FUND_CLAIM_AMOUNT) {
          setTrialFundClaimedSeen(true);
          this.dusdBalance = recovered;
          this.claimMintStatus = 'pending';
          this.applyShowFeedCard(false);
          this.loggedView = 'closed';
          clearLoggedMiniSession();
          this.clearPendingClaimAfterWin();
          await Promise.allSettled([this.refreshConditions()]);
          trackAnalyticsLog({
            key: 'trial_fund.claim',
            label: '领取试玩金',
            value: { recoveredAfterError: true, amount: TRIAL_FUND_CLAIM_AMOUNT },
          });
          return { ok: true, recovered: true, mintStatus: 'pending' };
        }

        this.setSurfaceHint(userMessage, 'error');
        return { ok: false, message: userMessage };
      } finally {
        this.claimLoading = false;
      }
    },

    /**
     * 充值成功 / Privy 就绪后：若本会话（或持久化标记）已赢未领，自动签领体验金。
     * 不依赖结果窗是否仍打开；toast 由调用方按 `attempted` 决定（保持 store 无 UI 依赖）。
     * @returns {Promise<{ ok: boolean, needDeposit?: boolean, alreadyClaimed?: boolean, attempted?: boolean, auto?: boolean }>}
     */
    async retryClaimAfterDepositIfNeeded() {
      this.hydratePendingClaimAfterWinFromStorage();
      if (this.hasClaimed || readTrialFundClaimedSeen()) {
        this.clearPendingClaimAfterWin();
        return { ok: true };
      }
      if (!this.hasPendingClaimAfterWin()) {
        return { ok: false };
      }
      try {
        await this.refreshConditions();
      } catch {
        /* continue with prior state */
      }
      this.syncClaimNeedsDepositFromConditions();
      if (isKnownMissingDeposit(this.conditions)) {
        return { ok: false, needDeposit: true };
      }
      const out = await this.claimAfterWin();
      console.info('[trial-fund] auto claim after deposit', {
        ok: Boolean(out?.ok),
        alreadyClaimed: Boolean(out?.alreadyClaimed),
        needDeposit: Boolean(out?.needDeposit),
      });
      return { ...out, auto: true, attempted: true };
    },

    syncSimClockWindow(now = getClobNowMs()) {
      const { endMs } = resolveTrialFundRoundWindow(now);
      this.simNowMs = now;
      this.simRoundEndAtMs = endMs;
      if (this.simPhase === 'pending') {
        this.simSettleAtMs = endMs;
      }
    },

    ensureSimRoundWatcher() {
      if (typeof window === 'undefined') return;
      simWatcherStore = this;
      if (simRoundTimerId) return;
      this.syncSimClockWindow();
      simRoundTimerId = window.setInterval(() => {
        simWatcherStore?.tickSimRound();
      }, 250);
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', onSimDocumentVisible);
      }
    },

    stopSimRoundWatcher({ force = false } = {}) {
      if (!force && (this.simPhase === 'pending' || this.simPhase === 'settling')) return;
      if (simRoundTimerId) {
        window.clearInterval(simRoundTimerId);
        simRoundTimerId = 0;
      }
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onSimDocumentVisible);
      }
      simWatcherStore = null;
    },

    tickSimRound() {
      const now = getClobNowMs();
      this.simNowMs = now;

      if (this.simPhase === 'pending' && this.simLockedSide) {
        void this.settleSimRound();
        return;
      }

      if (this.simPhase === 'idle') {
        const { endMs } = resolveTrialFundRoundWindow(now);
        this.simRoundEndAtMs = endMs;
      }
    },

    /**
     * 从 session 恢复 pending（刷新 / 登录后）
     * @returns {boolean}
     */
    restoreSimPendingFromSession() {
      if (this.simPhase === 'pending' || this.simPhase === 'settling') {
        this.ensureSimRoundWatcher();
        if (this.simPhase === 'pending') void this.settleSimRound();
        return true;
      }
      const saved = readTrialFundSimPending();
      if (!saved) return false;
      if (readTrialFundClaimedSeen() || this.conditions?.claimed) {
        clearTrialFundSimPending();
        return false;
      }
      this.simPhase = 'pending';
      this.simLockedSide = saved.side;
      this.simLockedShares = saved.shares || TRIAL_FUND_SHARES;
      this.simSettleAtMs = saved.settleAtMs;
      this.simRoundEndAtMs = saved.settleAtMs;
      this.simResolvedSide = null;
      simSettleOnce = false;
      this.loggedView = 'mini';
      persistLoggedMiniSession();
      this.ensureSimRoundWatcher();
      void this.settleSimRound();
      return true;
    },

    /**
     * 模拟盘买入锁定持仓
     * @param {{ side: 'yes' | 'no', shares?: number }} payload
     * @returns {{ ok: boolean, code?: string }}
     */
    lockSimPosition({ side, shares = TRIAL_FUND_SHARES } = {}) {
      if (readTrialFundClaimedSeen() || this.conditions?.claimed) {
        return { ok: false, code: 'alreadyClaimed' };
      }
      if (this.simPhase !== 'idle') {
        return { ok: false, code: 'alreadyPending' };
      }
      const nextSide = side === 'no' ? 'no' : 'yes';
      const now = getClobNowMs();
      const { endMs } = resolveTrialFundRoundWindow(now);
      this.simPhase = 'pending';
      this.simLockedSide = nextSide;
      this.simLockedShares = Number(shares) > 0 ? Number(shares) : TRIAL_FUND_SHARES;
      this.simNowMs = now;
      this.simRoundEndAtMs = endMs;
      this.simSettleAtMs = endMs;
      this.simResolvedSide = null;
      simSettleOnce = false;
      writeTrialFundSimPending({
        side: nextSide,
        settleAtMs: endMs,
        shares: this.simLockedShares,
      });
      this.loggedView = 'mini';
      persistLoggedMiniSession();
      this.ensureSimRoundWatcher();
      void this.settleSimRound();
      return { ok: true };
    },

    /** 卖出 / 撤纸面仓 */
    cancelSimPosition() {
      this.resetSimRound({ keepWatcher: false });
    },

    /**
     * @param {{ keepWatcher?: boolean }} [options]
     */
    resetSimRound({ keepWatcher = false } = {}) {
      this.simPhase = 'idle';
      this.simLockedSide = null;
      this.simLockedShares = 0;
      this.simResolvedSide = null;
      this.simSettleAtMs = 0;
      simSettleOnce = false;
      simSettleInflight = false;
      clearTrialFundSimPending();
      this.syncSimClockWindow();
      if (!keepWatcher) this.stopSimRoundWatcher({ force: true });
      else this.ensureSimRoundWatcher();
    },

    /** 输赢结果弹窗关闭后：回到 idle，便于再来一次 */
    acknowledgeSimResult() {
      if (this.simPhase !== 'settled' && this.simPhase !== 'settling') return;
      this.resetSimRound({ keepWatcher: false });
    },

    async settleSimRound() {
      if (simSettleOnce || simSettleInflight) return;
      if (this.simPhase !== 'pending' || !this.simLockedSide) return;
      simSettleOnce = true;
      simSettleInflight = true;
      this.simPhase = 'settling';
      this.simResolvedSide = TRIAL_FUND_CORRECT_SIDE;
      clearTrialFundSimPending();

      const side = this.simLockedSide;
      const won = side === TRIAL_FUND_CORRECT_SIDE;

      try {
        if (won) {
          this.simPhase = 'settled';
          this.simLockedShares = 0;
          if (this.guideOpen) {
            this.pauseGuideForResult();
          }
          // 先落持久化待领标记，再自动 claim（未充值会失败并保留标记，供充值后静默补领）
          this.markPendingClaimAfterWin();
          this.openResultDialog({ kind: 'win', chosenSide: side });
          void this.claimAfterWin();
          return;
        }

        this.simPhase = 'settled';
        this.simLockedShares = 0;
        if (this.guideOpen) {
          this.pauseGuideForResult();
        }
        this.openResultDialog({ kind: 'lose', chosenSide: side });
      } finally {
        simSettleInflight = false;
      }
    },

    resetOnLogout() {
      this.guestView = 'closed';
      clearGuestMiniSession();
      this.loggedView = 'closed';
      clearLoggedMiniSession();
      this.resultOpen = false;
      this.conditions = null;
      this.dusdBalance = 0;
      this.claimMintStatus = '';
      this.clearPendingClaimAfterWin();
      this.clearSurfaceHint();
      this.resetSimRound({ keepWatcher: false });
      this.closeGuide({ persistDone: false });
      // 用户态本地标记随登出清掉，避免换号串引导 / 已兑换态
      clearTrialFundUserStorage();
      this.applyShowFeedCard(false);
    },

    /**
     * 首次进入福利盘详情（H5）：展开游戏式引导（游客 / 登录均可）
     * @returns {boolean}
     */
    tryStartSimGuide() {
      if (typeof window === 'undefined') return false;
      if (!TRIAL_FUND_GUIDE_ENABLED) return false;
      if (this.guideOpen) return true;
      if (this.guidePausedForAuth || this.guidePausedForResult) return false;
      if (readTrialFundGuideDone(getUserId())) return false;
      if (hasTrialFundClaimLifecycle(this.conditions)) return false;
      return this.openSimGuide({ trackStart: true });
    },

    /**
     * 已领取用户：关闭引导（含登录暂关）并持久化为已走过。
     * @returns {boolean}
     */
    finishGuideIfAlreadyClaimed() {
      if (!hasTrialFundClaimLifecycle(this.conditions)) return false;
      if (!this.guideOpen && !this.guidePausedForAuth && !this.guidePausedForResult) {
        return false;
      }
      setTrialFundClaimedSeen(true);
      this.closeGuide({ persistDone: true });
      return true;
    },

    /**
     * @param {{ trackStart?: boolean }} [options]
     * @returns {boolean}
     */
    openSimGuide({ trackStart = true } = {}) {
      if (this.guestView === 'modal') this.guestView = 'closed';
      if (this.loggedView === 'modal') {
        this.loggedView = 'mini';
        persistLoggedMiniSession();
      }
      this.guidePausedForAuth = false;
      this.guideStepBeforeAuth = '';
      this.guidePausedForResult = false;
      this.guideStepBeforeResult = '';
      this.resultOpen = false;
      this.guideOpen = true;
      this.guideStep = 'rules';
      this.guideStepBeforeDismiss = '';
      if (trackStart) {
        try {
          tryTrackOnboardingGuideStart();
        } catch (err) {
          console.warn('[trial-fund] guide_start analytics failed', err);
        }
      }
      return true;
    },

    /** 点击下单唤起登录：暂时关闭引导（不记完成） */
    pauseGuideForAuth() {
      if (!this.guideOpen && !this.guidePausedForAuth) return;
      if (this.guideOpen) {
        this.guideStepBeforeAuth =
          this.guideStep === 'dismissConfirm'
            ? this.guideStepBeforeDismiss || 'guess'
            : this.guideStep || 'guess';
        this.guideOpen = false;
        this.guideStep = '';
        this.guideStepBeforeDismiss = '';
      }
      this.guidePausedForAuth = true;
    },

    /**
     * 登录窗关闭或登录成功后：先刷领取态；已领取则记引导完成并退出，否则恢复引导。
     * @returns {Promise<boolean>}
     */
    async resumeGuideAfterAuth() {
      if (resumeGuideAfterAuthInflight) return resumeGuideAfterAuthInflight;
      if (!this.guidePausedForAuth) return false;

      resumeGuideAfterAuthInflight = (async () => {
        try {
          if (!TRIAL_FUND_GUIDE_ENABLED) {
            this.guidePausedForAuth = false;
            this.guideStepBeforeAuth = '';
            return false;
          }

          if (readTrialFundGuideDone(getUserId()) || this.finishGuideIfAlreadyClaimed()) {
            this.guidePausedForAuth = false;
            this.guideStepBeforeAuth = '';
            return false;
          }

          try {
            await this.refreshConditions();
          } catch {
            /* 条件失败时仍按本地 / 已有 state 判断 */
          }

          // refreshConditions 内可能已 finishGuideIfAlreadyClaimed
          if (!this.guidePausedForAuth) return false;
          if (readTrialFundGuideDone(getUserId()) || this.finishGuideIfAlreadyClaimed()) {
            return false;
          }

          const step = this.guideStepBeforeAuth || 'guess';
          this.guidePausedForAuth = false;
          this.guideStepBeforeAuth = '';
          if (this.guestView === 'modal') this.guestView = 'closed';
          if (this.loggedView === 'modal') {
            this.loggedView = 'mini';
            persistLoggedMiniSession();
          }
          this.guideOpen = true;
          this.guideStep = step;
          this.guideStepBeforeDismiss = '';
          return true;
        } finally {
          resumeGuideAfterAuthInflight = null;
        }
      })();

      return resumeGuideAfterAuthInflight;
    },

    /** 引导中结算出结果：暂隐引导，让结果窗独占 */
    pauseGuideForResult() {
      if (!this.guideOpen && !this.guidePausedForResult) return;
      if (this.guideOpen) {
        this.guideStepBeforeResult =
          this.guideStep === 'dismissConfirm'
            ? this.guideStepBeforeDismiss || 'guess'
            : this.guideStep || 'guess';
        this.guideOpen = false;
        this.guideStep = '';
        this.guideStepBeforeDismiss = '';
      }
      this.guidePausedForResult = true;
    },

    /**
     * 赢后「查看余额」：恢复到客服引导步（原兑换弹窗后的 kefu）。
     * @returns {boolean}
     */
    resumeGuideAtKefuAfterWin() {
      if (!this.guidePausedForResult) return false;
      if (!TRIAL_FUND_GUIDE_ENABLED) {
        this.guidePausedForResult = false;
        this.guideStepBeforeResult = '';
        return false;
      }
      this.guidePausedForResult = false;
      this.guideStepBeforeResult = '';
      if (this.guestView === 'modal') this.guestView = 'closed';
      if (this.loggedView === 'modal') {
        this.loggedView = 'mini';
        persistLoggedMiniSession();
      }
      this.guideOpen = true;
      this.guideStep = 'kefu';
      this.guideStepBeforeDismiss = '';
      return true;
    },

    /**
     * 「再来一次」：恢复引导到竞猜步。
     * 首页开奖（feedCountdown）输掉时由 App 跳进福利盘详情；赢逻辑不走这里。
     * @returns {boolean}
     */
    resumeGuideAfterLoseRetry() {
      if (!this.guidePausedForResult) return false;
      if (!TRIAL_FUND_GUIDE_ENABLED) {
        this.guidePausedForResult = false;
        this.guideStepBeforeResult = '';
        return false;
      }
      this.guidePausedForResult = false;
      this.guideStepBeforeResult = '';
      if (this.guestView === 'modal') this.guestView = 'closed';
      if (this.loggedView === 'modal') {
        this.loggedView = 'mini';
        persistLoggedMiniSession();
      }
      // 统一回竞猜步（勿回 exit/feedCountdown，避免停在首页无法下单）
      this.guideOpen = true;
      this.guideStep = 'guess';
      this.guideStepBeforeDismiss = '';
      return true;
    },

    /** 主流程下一步 */
    advanceGuide() {
      if (!this.guideOpen) return;
      if (this.guideStep === 'dismissBye' || this.guideStep === 'bye') {
        this.finishGuide({
          outcome: this.guideStep === 'dismissBye' ? 'skip' : 'complete',
        });
        return;
      }
      if (this.guideStep === 'dismissConfirm') return;
      const idx = TRIAL_FUND_GUIDE_STEPS.indexOf(
        /** @type {typeof TRIAL_FUND_GUIDE_STEPS[number]} */ (this.guideStep)
      );
      if (idx < 0) {
        this.finishGuide();
        return;
      }
      if (idx >= TRIAL_FUND_GUIDE_STEPS.length - 1) {
        this.finishGuide();
        return;
      }
      this.guideStep = TRIAL_FUND_GUIDE_STEPS[idx + 1];
    },

    /** 竞猜成功：即时开奖，由结果窗接管，不再进倒计时 / 回首页 */
    onGuideGuessPlaced() {
      return;
    },

    /** 离开模拟盘：已跳过首页倒计时步 */
    onGuideLeftSimPage() {
      return;
    },

    requestGuideDismiss() {
      if (!this.guideOpen) return;
      if (this.guideStep === 'dismissConfirm' || this.guideStep === 'dismissBye') return;
      this.guideStepBeforeDismiss = this.guideStep;
      this.guideStep = 'dismissConfirm';
    },

    cancelGuideDismiss() {
      if (this.guideStep !== 'dismissConfirm') return;
      this.guideStep = this.guideStepBeforeDismiss || 'rules';
      this.guideStepBeforeDismiss = '';
    },

    confirmGuideDismiss() {
      if (this.guideStep !== 'dismissConfirm') return;
      this.guideStep = 'dismissBye';
      this.guideStepBeforeDismiss = '';
    },

    finishGuide({ outcome } = {}) {
      const step = this.guideStep;
      const resolved =
        outcome === 'skip' || outcome === 'complete'
          ? outcome
          : step === 'dismissBye'
          ? 'skip'
          : 'complete';
      try {
        tryTrackOnboardingGuideOutcome(resolved, { step });
      } catch (err) {
        console.warn('[trial-fund] guide outcome analytics failed', err);
      }
      setTrialFundGuideDone(getUserId(), true);
      this.closeGuide({ persistDone: true });
      // 登录后进福利盘时大窗/FC 被引导压住；引导结束后再补弹登录后大窗
      this.openLoggedModalAfterGuide();
    },

    /**
     * 引导结束后弹出登录后大窗（进盘引导期间故意不弹）。
     * 已领取体验金：关闭；未领取：弹大窗引导进福利盘。
     */
    openLoggedModalAfterGuide() {
      if (!getUserId()) return;
      if (readTrialFundExchanged()) {
        this.loggedView = 'closed';
        clearLoggedMiniSession();
        return;
      }
      if (hasTrialFundClaimLifecycle(this.conditions)) {
        this.loggedView = 'closed';
        clearLoggedMiniSession();
        return;
      }
      this.applyShowFeedCard(true);
      this.loggedView = 'modal';
      clearLoggedMiniSession();
      trackAnalyticsLog({
        key: 'trial_fund.open',
        label: '打开试玩金',
        value: { audience: 'logged', view: 'modal', source: 'after_guide' },
      });
    },

    closeGuide({ persistDone = false } = {}) {
      if (persistDone) setTrialFundGuideDone(getUserId(), true);
      this.guideOpen = false;
      this.guideStep = '';
      this.guideStepBeforeDismiss = '';
      this.guidePausedForAuth = false;
      this.guideStepBeforeAuth = '';
      this.guidePausedForResult = false;
      this.guideStepBeforeResult = '';
    },
  },
});
